import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Activity, Sparkles, ShieldCheck, TrendingUp } from "lucide-react";

export default function Login() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        const { data } = await api.post("/auth/register", { email, password, name });
        setUser(data);
      }
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0F172A]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 border-r border-white/10 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#38BDF8]/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#10B981]/10 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#38BDF8] flex items-center justify-center">
            <Activity className="w-6 h-6 text-[#0F172A]" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display text-lg font-semibold text-white leading-tight">Dr. Awish Clinic</p>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-400">Marketing Command Center</p>
          </div>
        </div>
        <div className="relative z-10 space-y-8">
          <h1 className="font-display text-4xl xl:text-5xl font-bold text-white tracking-tight leading-[1.1]">
            Every rupee,<br />
            <span className="text-[#38BDF8]">optimized by AI.</span>
          </h1>
          <p className="text-slate-400 text-base max-w-md leading-relaxed">
            Real-time Meta & Google Ads ROI, service-wise attribution across Hair, Skin,
            Laser & Surgery, and an AI strategist that reallocates budget for you.
          </p>
          <div className="space-y-4">
            {[
              [TrendingUp, "Live spend pacing & burn-rate alerts"],
              [Sparkles, "AI budget reallocation & fatigue detection"],
              [ShieldCheck, "Service-level CPL, CPA & blended ROAS"],
            ].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                <div className="w-8 h-8 rounded-lg bg-[#1E293B] border border-white/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#10B981]" />
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© 2026 Dr. Awish Aesthetic Clinic</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#38BDF8] flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#0F172A]" strokeWidth={2.5} />
            </div>
            <p className="font-display text-lg font-semibold text-white">Dr. Awish Clinic</p>
          </div>
          <h2 className="font-display text-2xl font-semibold text-white mb-1">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-sm text-slate-400 mb-8">
            {mode === "login" ? "Sign in to your marketing dashboard" : "Register a viewer account"}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300 text-xs uppercase tracking-wider">Name</Label>
                <Input id="name" data-testid="register-name-input" value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-[#1E293B] border-white/10 text-white h-11 focus-visible:ring-[#38BDF8]" required />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com"
                className="bg-[#1E293B] border-white/10 text-white h-11 focus-visible:ring-[#38BDF8]" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="bg-[#1E293B] border-white/10 text-white h-11 focus-visible:ring-[#38BDF8]" required />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="login-submit-btn" disabled={busy}
              className="w-full h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full transition-colors">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-slate-400 mt-6 text-center">
            {mode === "login" ? "Need an account?" : "Already have one?"}{" "}
            <button data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-[#38BDF8] hover:underline font-medium">
              {mode === "login" ? "Register" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
